import {
  EXTS_BY_KIND,
  kindOf,
  SPINDLE_SPEED_MIN,
  SPINDLE_SPEED_MAX,
} from '@/redux/post/post.constants';
import type { V2Bit, V2File, V2Operation } from '../v2Upload.d';
import { VENDOR_POSTS } from './vectricPosts';

const { image: IMG_EXTS, gcode: GCODE_EXTS } = EXTS_BY_KIND;

export { kindOf };

/** What each section's Add button pre-filters the file dialog to. Docs are the
 * catch-all kind, so that one filters nothing. A picked file is still sorted by
 * `kindOf` — the filter narrows the dialog, it doesn't decide the section. */
export const ACCEPT_BY_KIND: Record<V2File['kind'], string | undefined> = {
  image: IMG_EXTS.map((ext) => `.${ext}`).join(','),
  gcode: GCODE_EXTS.map((ext) => `.${ext}`).join(','),
  doc: undefined,
};

export const DEFAULT_CONTROLLER = 'Redline';

/** The columns a G-code file can sit in. */
export const CONTROLLERS = ['Redline', 'Masso', 'Buildbotics'];

/** Stripped anywhere, as in `BB - Nutcrackers`; not `other`, an everyday word. */
const CONTROLLER_WORDS = [
  'redline',
  'redlne',
  'masso',
  'buildbotics',
  'buildotics',
  'bb',
];

/**
 * Every prefix `stripControllerPrefix` recognises, lower-cased.
 *
 * Wider than CONTROLLERS on purpose: `Unidentified` was a column once and posts
 * still carry it. `Buildotics` and `Redlne` are misspellings that exist in
 * saved file names, so removing either splits controller twins.
 */
const KNOWN_PREFIXES = [...CONTROLLER_WORDS, 'unidentified', 'other'];

/**
 * A file name with every controller prefix taken off the front, and the first
 * one that was there.
 *
 * Loops rather than stripping once: a file uploaded, downloaded and added back
 * carries the prefix already, and `uploadFileName` used to put a second one in
 * front of it — `Redline_Redline_Text.nc` is real, saved data. One pass would
 * leave half of it on screen.
 */
/** The leading `Redline_`-style word, or '' when the name opens with none. */
function leadingPrefix(name: string): string {
  const cut = name.indexOf('_');
  const head = cut > 0 ? name.slice(0, cut) : '';
  return KNOWN_PREFIXES.includes(head.toLowerCase()) ? head : '';
}

export function stripControllerPrefix(fileName: string): {
  name: string;
  prefix: string;
} {
  const prefixes: string[] = [];
  let name = fileName;
  for (let head = leadingPrefix(name); head; head = leadingPrefix(name)) {
    prefixes.push(head);
    name = name.slice(head.length + 1);
  }
  return { name, prefix: prefixes[0] ?? '' };
}

function controllerAgnosticLabel(fileName: string): string {
  const base = stripControllerPrefix(fileName)
    .name.replace(/\.[^.]+$/, '')
    .trim();
  const all = base.split(/[\s_-]+/);
  const named = all.filter((w) => !CONTROLLER_WORDS.includes(w.toLowerCase()));
  const words = named.length ? named : all;
  while (
    words.length > 1 &&
    KNOWN_PREFIXES.includes(words[words.length - 1].toLowerCase())
  ) {
    words.pop();
  }
  return words.join(' ');
}

/** The same cut under any controller, normalised for pairing. */
export function controllerAgnosticName(fileName: string): string {
  return controllerAgnosticLabel(fileName).toLowerCase();
}

/**
 * Extension-only guess, used to fill the card in before the parser has read the
 * file: the one controller whose vendor posts write that extension, else the
 * default. The parser's header read replaces this whenever it names a
 * controller, and the user can drag the file to another column.
 *
 * `.nc` is written by both Masso and Redline posts, so no extension rule can
 * pick between them. It lands on the default because the evidence is tied,
 * not because Redline is more likely. Only the header read can settle it.
 */
export function controllerOf(ext: string): string {
  // Masso `.gcode` shows Buildbotics until the header read corrects it.
  const owners = new Set(
    VENDOR_POSTS.filter((post) => post.ext === ext).map((p) => p.controller)
  );
  return owners.size === 1 ? [...owners][0] : DEFAULT_CONTROLLER;
}

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** The user-selected thumbnail, falling back to the first image when the
 * selection is stale (that file was removed) or was never made. Undefined
 * when there are no images at all — the thumbnail is optional. */
export function pickThumbnailFile(
  files: V2File[],
  thumbnailFileId: string | null
): V2File | undefined {
  const images = files.filter((f) => f.kind === 'image');
  return images.find((f) => f.id === thumbnailFileId) ?? images[0];
}

let uid = 1;
export function nextId(): string {
  const id = `f${uid}`;
  uid += 1;
  return id;
}

/** Identifies a picked bit: the same bit sold by two brands is two entries in
 * the catalog, and which one was picked is part of what gets saved. */
export function bitKey(bitId: string, brandId: string): string {
  return `${bitId}:${brandId}`;
}

/** A blank bit row, ready for the user to pick a catalog bit into. */
export function emptyBit(bitType = '', spindleSpeed = ''): V2Bit {
  return {
    id: `bit-${nextId()}`,
    bitType,
    bitId: '',
    brandId: '',
    bitName: '',
    spindleSpeed,
  };
}

/** The rpm as a number when it is inside the range the backend accepts, else
 * undefined — so an unset or out-of-range speed is simply not sent. */
export function validSpindle(speed: string): number | undefined {
  const spindle = Number(speed);
  const ok =
    Number.isFinite(spindle) &&
    spindle >= SPINDLE_SPEED_MIN &&
    spindle <= SPINDLE_SPEED_MAX;
  return ok ? spindle : undefined;
}

/** What a row still needs before its file counts as assigned. Drives the row
 * status and the gate on Continue, so the two can't disagree. */
export interface OpChecklist {
  hasBit: boolean;
  hasSpeeds: boolean;
  hasFile: boolean;
  complete: boolean;
  /** The first unmet requirement, spelled out. Null once complete. */
  missing: string | null;
}

/** `one` for a count of 1, `many` otherwise — for "1 bit" vs "2 bits". */
export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** "a", "a and b", "a, b and c" — the readable join for a "what's still
 * missing" hint. Shared by Basics and Job details so both steps phrase their
 * gate the same way; a spec that pins one sentence pins both. */
export function formatList(items: string[]): string {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// Shown in the group's banner, so each says what to do and where — a bare
// "Add at least one bit" names the gap without naming the way out of it.
function firstMissing(
  hasBit: boolean,
  hasSpeeds: boolean,
  hasFile: boolean
): string | null {
  if (!hasBit) return 'Pick the bit or laser this file was cut with.';
  if (!hasSpeeds) return 'Every bit needs a spindle speed — fill in the rpm.';
  if (!hasFile) return 'This operation has no G-code file.';
  return null;
}

export function opChecklist(op: V2Operation): OpChecklist {
  const picked = op.bits.filter((bit) => bit.bitId);
  const hasBit = picked.length > 0;
  const hasSpeeds =
    hasBit && picked.every((bit) => validSpindle(bit.spindleSpeed));
  const hasFile = op.fileIds.length > 0;
  return {
    hasBit,
    hasSpeeds,
    hasFile,
    complete: hasBit && hasSpeeds && hasFile,
    missing: firstMissing(hasBit, hasSpeeds, hasFile),
  };
}

/**
 * The operation a G-code file belongs to: its name with the controller taken
 * off both ends.
 *
 * The same cut is posted once per controller — `Mallet Handle Masso.nc`,
 * `Mallet Handle BB.ngc`, `Redline_Mallet Handle Redline.nc` — and those files
 * are one tool group by definition: the same cut, so the same bits. What is left
 * once the controller comes off is the operation.
 *
 * The file name is used rather than anything inside the G-code because it is the
 * only signal all three variants share. Buildbotics' post processor writes no
 * project name into its header at all, and one Redline header disagrees with its
 * own file name (`Mallet Sides Outer Text` on disk, `(Mallet Outer Text)`
 * inside).
 */
function operationNameOf(fileName: string): string {
  return controllerAgnosticLabel(fileName);
}

/** How many leading words every name shares, capped one short of the shortest
 * so no name can be reduced to nothing. */
function sharedWordCount(words: string[][]): number {
  const cap = Math.max(Math.min(...words.map((w) => w.length)) - 1, 0);
  const differs = words[0]
    .slice(0, cap)
    .findIndex((word, i) =>
      words.some((w) => w[i].toLowerCase() !== word.toLowerCase())
    );
  return differs === -1 ? cap : differs;
}

/**
 * The words every group's name opens with, dropped — CAM software names its
 * files after the project, so `Whiskey Smoker 2 Text` and `Whiskey Smoker 2
 * Finishing` become `Text` and `Finishing`. The listing's own title already
 * says which project this is.
 */
function dropSharedPrefix(names: string[]): string[] {
  if (names.length < 2) return names;
  const words = names.map((name) => name.split(/\s+/));
  const short = words.map((w) => w.slice(sharedWordCount(words)).join(' '));
  // `Trivet 2` beside `Trivet 2 Profile` leaves a bare "2" — a fragment names
  // the cut worse than the repetition did, so that project keeps its full names.
  return short.some((name) => name.length < 3) ? names : short;
}

/** A group with no bits shows just the "Add bits" button —
 * seeding an empty row would put a second, identical button above it. Rows only
 * start out empty when the parser named a bit type to pick against. */
function newOperation(
  name: string,
  bits: V2Bit[] = [],
  fileIds: string[] = []
): V2Operation {
  return { id: `op-${nextId()}`, name, bits, fileIds };
}

/**
 * One operation per G-code file, in the order the files are held, keeping any
 * assignment an existing operation already carries for that file.
 *
 * An operation holding SEVERAL files is split, one copy per file, keeping its
 * name and bits. Drafts written by the old flow grouped the same cut posted for
 * three controllers into one group — left whole, the three files would share a
 * row, and the row would show one file's name and controller for all of them.
 *
 * The name is what the file's own name calls the cut, with the project prefix
 * every CAM export repeats dropped — `Whiskey Smoker 2 Text` becomes `Text`.
 */
export function operationsForFiles(
  files: V2File[],
  existing: V2Operation[] = []
): V2Operation[] {
  const gcode = files.filter((f) => f.kind === 'gcode');
  const short = dropSharedPrefix(gcode.map((f) => operationNameOf(f.name)));
  const claimed = new Set<string>();

  return gcode.map((file, i) => {
    const held = existing.find((op) => op.fileIds.includes(file.id));
    if (!held) return newOperation(short[i], [], [file.id]);
    if (claimed.has(held.id)) {
      return { ...newOperation(held.name, [...held.bits]), fileIds: [file.id] };
    }
    claimed.add(held.id);
    return held.fileIds.length === 1 ? held : { ...held, fileIds: [file.id] };
  });
}
